import { tinykeys } from "tinykeys";
import { useEffect, useRef } from "preact/hooks";

const handledShortcutEvent = Symbol("handledShortcutEvent");

function alternatePhysicalBinding(bindingKey) {
  const parts = String(bindingKey ?? "").trim().split("+");
  if (parts.length === 0) {
    return null;
  }

  const key = parts[parts.length - 1];
  if (parts.includes("Alt") && /^[A-Za-z]$/.test(key)) {
    return [...parts.slice(0, -1), `Key${key.toUpperCase()}`].join("+");
  }

  if (key === "/") {
    return [...parts.slice(0, -1), "Slash"].join("+");
  }

  return null;
}

function isTypingTarget(target) {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  return target.isContentEditable
    || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

function resolveBindingTarget(target) {
  if (!target) {
    return null;
  }

  if ("current" in target) {
    return target.current;
  }

  return target;
}

export function useShortcutBindings(target, bindings) {
  const bindingsRef = useRef(new Map());
  const bindingKeysRef = useRef([]);
  const bindingEntries = bindings
    .filter((binding) => binding?.binding)
    .flatMap((binding) => {
      const primaryKey = binding.binding;
      const alternateKey = alternatePhysicalBinding(primaryKey);
      return alternateKey
        ? [[primaryKey, binding], [alternateKey, binding]]
        : [[primaryKey, binding]];
    });
  const bindingKeys = bindingEntries.map(([bindingKey]) => bindingKey);
  const bindingKeysKey = bindingKeys.join("\u0000");

  bindingsRef.current = new Map(bindingEntries);
  bindingKeysRef.current = bindingKeys;

  useEffect(() => {
    const bindingTarget = resolveBindingTarget(target);
    if (!bindingTarget) {
      return undefined;
    }

    const activeBindingKeys = bindingKeysRef.current;
    if (activeBindingKeys.length === 0) {
      return undefined;
    }

    const keyBindingMap = Object.fromEntries(
      activeBindingKeys.map((bindingKey) => [
        bindingKey,
        (event) => {
          if (event[handledShortcutEvent]) {
            return;
          }

          const binding = bindingsRef.current.get(bindingKey);
          if (typeof binding?.onTrigger !== "function") {
            return;
          }

          if (!binding.allowInInput && isTypingTarget(event.target)) {
            return;
          }

          event[handledShortcutEvent] = true;
          event.preventDefault();
          binding.onTrigger(event);
        }
      ])
    );

    return tinykeys(bindingTarget, keyBindingMap);
  }, [bindingKeysKey, target]);
}
